import App from '@/App'
import '@/App.css'
import '@/assets/css/common.css'
import '@/components.css'
import '@/custom.css'
import '@/index.css'
import reportWebVitals from '@/reportWebVitals'
import '@/screens.css'
import store, { msalInstance } from '@/store'
import { isUserbackEnabled } from '@/utilities/utils'
import { EventType } from '@azure/msal-browser'
import { UserbackProvider } from '@userback/react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'

// export const msalInstance = new PublicClientApplication(msalConfig)

const USERBACK_BUTTON_POSITION_KEY = 'userback_button_position'
const DEFAULT_BUTTON_RIGHT = 324

const setupUserbackButtonDrag = () => {
  const initDrag = (button) => {
    if (!button || button.dataset.dragInitialized) return

    button.dataset.dragInitialized = 'true'

    let isDragging = false
    let hasDragged = false
    let startX = 0
    let startLeft = 0

    // Free drag: the only bound is the viewport itself (so the button can't be lost
    // off-screen). Earlier limits against the scratchpad button and the Quick Order /
    // Mic Capture sidebar kept collapsing the range and pinning the button in place;
    // it sits above the sidebar (z-index in DashboardQuickOrder.css), so no limit is needed.
    const clampLeft = (left) => {
      const btnW = button.offsetWidth || 120
      const maxLeft = Math.max(0, window.innerWidth - btnW)
      return Math.max(0, Math.min(maxLeft, left))
    }

    const applyLeft = (left) => {
      const clamped = clampLeft(left)
      button.style.left = `${clamped}px`
      return clamped
    }

    // Calculate the ideal position between search and the first obstacle to its right
    const getIdealLeft = () => {
      // Left boundary: right edge of the search area
      const searchBtn = document.querySelector('.global-search-btn')
      const searchEl = document.querySelector('.inputSearch') || document.querySelector('#global-search-input')
      const leftEdge = searchBtn ? searchBtn.getBoundingClientRect().right : searchEl ? searchEl.getBoundingClientRect().right : 0
      if (!leftEdge) return null

      const btnW = button.offsetWidth || 120

      // Right boundary: leftmost of scratchpad button or navIcons
      const scratchpadBtn = document.querySelector('.scratchpad-button')
      const navIconsEl = document.querySelector('.navIcons')

      let rightEdge = window.innerWidth // fallback
      if (scratchpadBtn && scratchpadBtn.offsetParent !== null) {
        // Scratchpad exists and is visible
        rightEdge = scratchpadBtn.getBoundingClientRect().left
      } else if (navIconsEl) {
        rightEdge = navIconsEl.getBoundingClientRect().left
      }

      const gap = rightEdge - leftEdge
      const padding = 12 // min gap on each side

      if (gap < btnW + padding * 2) {
        // Not enough space — push flush left of the right boundary
        return clampLeft(Math.round(rightEdge - btnW - padding))
      }

      // Center in the available gap
      const idealLeft = leftEdge + (gap - btnW) / 2
      return clampLeft(Math.round(idealLeft))
    }

    // Restore saved position, or auto-center after layout is ready
    const saved = localStorage.getItem(USERBACK_BUTTON_POSITION_KEY)
    if (saved) {
      applyLeft(parseInt(saved, 10))
    } else {
      // Auto-center once the header elements are in the DOM
      requestAnimationFrame(() => {
        const ideal = getIdealLeft()
        if (ideal !== null) {
          applyLeft(ideal)
        }
      })
    }

    // Re-check position when scratchpad button appears (it mounts later via React)
    const scratchpadObserver = new MutationObserver(() => {
      const scratchpadBtn = document.querySelector('.scratchpad-button')
      if (scratchpadBtn) {
        scratchpadObserver.disconnect()
        // A spot the user dragged to is respected, even over the scratchpad button
        if (localStorage.getItem(USERBACK_BUTTON_POSITION_KEY)) return
        // Only auto-adjust if we overlap
        const feedbackRect = button.getBoundingClientRect()
        const scratchpadRect = scratchpadBtn.getBoundingClientRect()
        if (feedbackRect.right > scratchpadRect.left - 8) {
          const ideal = getIdealLeft()
          if (ideal !== null) {
            applyLeft(ideal)
            localStorage.setItem(USERBACK_BUTTON_POSITION_KEY, ideal)
          }
        }
      }
    })
    scratchpadObserver.observe(document.body, { childList: true, subtree: true })

    // No sidebar open/close repositioning any more — the button stays wherever the user
    // dragged it, sidebar or not.

    // Pointer events + pointer capture: the drag keeps receiving moves/up even when the
    // cursor crosses an iframe (Userback widget, embedded viewers) or leaves the window —
    // with plain mouse events the release was lost there and the drag got stuck. Also
    // makes the button draggable on touch / pen.
    let activePointerId = null

    const handlePointerDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return

      isDragging = true
      hasDragged = false
      startX = e.clientX
      startLeft = button.offsetLeft
      activePointerId = e.pointerId
      try {
        button.setPointerCapture(e.pointerId)
      } catch {
        // capture can fail if the pointer is already gone; document listeners still cover it
      }

      button.classList.add('dragging')

      e.preventDefault()
      e.stopPropagation()
    }

    const handlePointerMove = (e) => {
      if (!isDragging || e.pointerId !== activePointerId) return
      // Missed the release (e.g. alt-tab mid-drag) — end instead of dragging with no button held
      if (e.pointerType === 'mouse' && e.buttons === 0) {
        handlePointerUp()
        return
      }

      const deltaX = e.clientX - startX

      if (Math.abs(deltaX) > 3) {
        hasDragged = true
      }

      const newLeft = startLeft + deltaX
      applyLeft(newLeft)
    }

    const handlePointerUp = () => {
      if (!isDragging) return

      isDragging = false
      button.classList.remove('dragging')
      if (activePointerId !== null && button.hasPointerCapture?.(activePointerId)) {
        button.releasePointerCapture(activePointerId)
      }
      activePointerId = null

      const finalLeft = clampLeft(button.offsetLeft)
      button.style.left = `${finalLeft}px`

      // Only persist an actual drag — a plain click shouldn't overwrite the saved spot
      if (hasDragged) localStorage.setItem(USERBACK_BUTTON_POSITION_KEY, finalLeft)
    }

    const handleClick = (e) => {
      if (hasDragged) {
        e.preventDefault()
        e.stopPropagation()
        e.stopImmediatePropagation()
        hasDragged = false
      }
    }

    const handleDblClick = (e) => {
      e.preventDefault()
      e.stopPropagation()
      e.stopImmediatePropagation()
      // Reset to ideal centered position, or fallback to default
      const ideal = getIdealLeft()
      if (ideal !== null) {
        applyLeft(ideal)
      } else {
        const defaultLeft = clampLeft(window.innerWidth - DEFAULT_BUTTON_RIGHT - button.offsetWidth)
        applyLeft(defaultLeft)
      }
      localStorage.removeItem(USERBACK_BUTTON_POSITION_KEY)
    }

    const handleResize = () => {
      // Keep the user's dragged spot (just re-clamped to the new width) instead of
      // overwriting it with the auto-centred one — previously any resize/zoom snapped the
      // button back and discarded where the user had moved it. Not persisted: a temporary
      // narrow window shouldn't permanently shift the saved position.
      const savedLeft = localStorage.getItem(USERBACK_BUTTON_POSITION_KEY)
      if (savedLeft) {
        applyLeft(parseInt(savedLeft, 10))
        return
      }
      const ideal = getIdealLeft()
      applyLeft(ideal !== null ? ideal : button.offsetLeft)
    }

    // Capture phase so Userback's own handlers on the inner content can't swallow the press
    button.addEventListener('pointerdown', handlePointerDown, true)
    button.addEventListener('click', handleClick, true)
    button.addEventListener('dblclick', handleDblClick, true)

    document.addEventListener('pointermove', handlePointerMove)
    document.addEventListener('pointerup', handlePointerUp)
    document.addEventListener('pointercancel', handlePointerUp)
    button.addEventListener('lostpointercapture', handlePointerUp)

    window.addEventListener('blur', handlePointerUp)

    window.addEventListener('resize', handleResize)
  }

  // Observe async Userback load
  const observer = new MutationObserver(() => {
    const button = document.querySelector('.userback-button-e')
    if (button) {
      initDrag(button)
    }
  })

  observer.observe(document.body, {
    childList: true,
    subtree: true
  })

  // Immediate check
  const existing = document.querySelector('.userback-button-e')
  if (existing) {
    initDrag(existing)
  }
}

const clearDashboardLocalStorage = () => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith('dashboard')) {
      localStorage.removeItem(key)
    }
  })
}

msalInstance.initialize().then(() => {
  // Default to using the first account if no account is active on page load
  if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
    // Account selection logic is app dependent. Adjust as needed for different use cases.
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0])
  }

  // Optional - This will update account state if a user signs in from another tab or window
  msalInstance.enableAccountStorageEvents()

  msalInstance.addEventCallback((event) => {
    // console.log('MSAL Event:', event)
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload.account) {
      const account = event.payload.account
      msalInstance.setActiveAccount(account)
      // clear local storage items starts with dashboard (used for storing dashboard filters)
      clearDashboardLocalStorage()
    }

    // handle redirect start
    if (event.eventType === EventType.HANDLE_REDIRECT_START) {
      // clear local storage items starts with dashboard (used for storing dashboard filters)
      clearDashboardLocalStorage()
    }
  })

  const container = document.getElementById('root')
  const root = ReactDOM.createRoot(container)
  const user = msalInstance?.getAllAccounts()[0]
  const user_data = {
    id: user && user?.localAccountId,
    info: {
      name: user && user?.name,
      email: user && user?.username
    }
  }

  root.render(
    // <React.StrictMode>
    <Provider store={store}>
      {isUserbackEnabled() ? (
        <UserbackProvider
          token={import.meta.env.VITE_USER_BACK_TOKEN}
          options={{
            user_data: user_data
          }}
        >
          <App msalInstance={msalInstance} />
        </UserbackProvider>
      ) : (
        <App msalInstance={msalInstance} />
      )}
    </Provider>
    // </React.StrictMode>
  )

  if (isUserbackEnabled()) {
    setupUserbackButtonDrag()
  }

  reportWebVitals()
})
